import { useState } from "react";

import { Box, Stack, Typography } from "@mui/material";

import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { PageLayout } from "layouts";

import { useAuthContext, useSwrFetch } from "../../hooks";
import { CreateCompany } from "./components/CreateCompany";
import { JoinCompany } from "./components/JoinCompany";

function getMostRecentlyRejectedCompanyName(
  joinRequests: ListJoinCompanyRequest[],
): string | undefined {
  const rejectedRequests = joinRequests
    .filter((joinRequest) => joinRequest.status === "REJECTED")
    .sort(
      (a, b) =>
        new Date(b.request.created_at).getTime() -
        new Date(a.request.created_at).getTime(),
    );
  return rejectedRequests[0]?.request.company.name;
}

function getPreviouslyRejectedCompanyIds(
  joinRequests: ListJoinCompanyRequest[],
): number[] {
  return joinRequests
    .filter((joinRequest) => joinRequest.status === "REJECTED")
    .map((joinRequest) => joinRequest.request.company.id);
}

export function CompanySetup() {
  const { isLoading: isUserLoading } = useAuthContext();

  const {
    data: joinRequests = [],
    error,
    loading,
  } = useSwrFetch<ListJoinCompanyRequest[]>({
    url: !isUserLoading ? "/join-company-requests" : null,
    errorMessage: "Failed to fetch company join requests",
  });

  const [justSubmittedJoinRequest, setJustSubmittedJoinRequest] =
    useState<boolean>(false);
  const [creatingCompany, setCreatingCompany] = useState<boolean>(false);

  const handleCreateCompanySelected = (): void => {
    setCreatingCompany(true);
  };

  const handleSearchCompanySelected = (): void => {
    setCreatingCompany(false);
  };

  /*
    CompanySetup component is only mounted if a user has no role associated with a company.
    Therefore we can safely 'block' the user from accessing the application with the
    'waiting for request to be approved' screen while they have a PENDING company join
    request. A REJECTED request does not block: the user is sent back to company search
    so they can submit a new request.
  */
  const hasPendingJoinRequest =
    justSubmittedJoinRequest ||
    joinRequests.some((joinRequest) => joinRequest.status === "PENDING");

  let setupStep = (
    <JoinCompany
      onCreateCompanySelected={handleCreateCompanySelected}
      onJoinRequestSent={() => setJustSubmittedJoinRequest(true)}
      previouslyRejectedByCompanyName={getMostRecentlyRejectedCompanyName(
        joinRequests,
      )}
      previouslyRejectedCompanyIds={getPreviouslyRejectedCompanyIds(
        joinRequests,
      )}
    />
  );
  if (hasPendingJoinRequest) {
    setupStep = (
      <Stack spacing={3}>
        <Typography textAlign="center" variant="h4">
          Company Join Request Sent
        </Typography>
        <Typography variant="h5">
          You will receive an email as soon as your company administrator has
          approved your request. For more information or to find out who your
          company administrator is, contact us anytime at info@evotrux.com
        </Typography>
      </Stack>
    );
  } else if (creatingCompany) {
    setupStep = (
      <CreateCompany
        handleSearchCompanySelected={handleSearchCompanySelected}
      />
    );
  }

  return (
    <PageLayout>
      <Stack alignItems="center">
        <Box
          width="100%"
          sx={{
            maxWidth: (theme) => theme.breakpoints.values.md,
            px: { xs: 2, sm: 3 },
            mt: { xs: "2rem", md: "4rem" },
          }}
        >
          {error != null ? (
            <ErrorTypography error={error} />
          ) : (
            <LoadingWrappedNode loading={isUserLoading || loading}>
              {setupStep}
            </LoadingWrappedNode>
          )}
        </Box>
      </Stack>
    </PageLayout>
  );
}
